import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Percent, CheckCircle2, Landmark, FileText, Settings as SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  getCompanyProfile, updateCompanyProfile, invalidateCompanyProfile
} from "@/utils/companyProfile";

// Fuera del componente para que React no lo remonte en cada render
// (si se define dentro, los inputs pierden el foco al teclear).
function Field({ label, required, children, help }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1 block text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
    </div>
  );
}

const STEPS = [
  { num: 1, label: "Tu empresa" },
  { num: 2, label: "Tu fiscalidad" },
  { num: 3, label: "Listo" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Bloques completos del perfil (para no machacar campos que no editamos aquí)
  const [profileIdentidad, setProfileIdentidad] = useState({});
  const [profileFiscalFacturas, setProfileFiscalFacturas] = useState({});
  const [profileFiscalAutonomo, setProfileFiscalAutonomo] = useState({});

  // ---- Paso 1: Tu empresa ----
  const [identidad, setIdentidad] = useState({
    tipo_persona: "fisica",
    name: "",
    nif: "",
    address: "",
    city: "",
    provincia: "",
    postal_code: "",
    email: "",
  });

  // ---- Paso 2: Tu fiscalidad ----
  const [fiscal, setFiscal] = useState({
    iva_default: "21",
    aplicar_irpf_default: false,
    irpf_default: "15",
    regimen_iva: "general",
    cuota_autonomo_mensual: "",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const p = await getCompanyProfile();
        if (cancelled) return;
        const id = p?.identidad ?? {};
        const ff = p?.fiscal_facturas ?? {};
        const fa = p?.fiscal_autonomo ?? {};
        setProfileIdentidad(id);
        setProfileFiscalFacturas(ff);
        setProfileFiscalAutonomo(fa);
        setIdentidad({
          tipo_persona: id.tipo_persona ?? "fisica",
          name: id.name ?? "",
          nif: id.nif ?? "",
          address: id.address ?? "",
          city: id.city ?? "",
          provincia: id.provincia ?? "",
          postal_code: id.postal_code ?? "",
          email: id.email ?? "",
        });
        setFiscal({
          iva_default: String(ff.iva_default ?? 21),
          aplicar_irpf_default: ff.aplicar_irpf_default ?? false,
          irpf_default: String(ff.irpf_default ?? 15),
          regimen_iva: ff.regimen_iva ?? "general",
          cuota_autonomo_mensual:
            fa.cuota_autonomo_mensual === null || fa.cuota_autonomo_mensual === undefined
              ? ""
              : String(fa.cuota_autonomo_mensual),
        });
      } catch {
        // Sin perfil cargado el wizard sigue funcionando con los valores por defecto
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const esFisica = identidad.tipo_persona === "fisica";

  const handleSaveStep1 = async () => {
    if (!identidad.name.trim() || !identidad.nif.trim() || !identidad.address.trim()) {
      toast.error("Nombre, NIF y dirección son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await updateCompanyProfile({
        identidad: { ...profileIdentidad, ...identidad },
      });
      invalidateCompanyProfile();
      setStep(2);
    } catch (e) {
      toast.error(e.message || "No se pudo guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStep2 = async () => {
    const irpf = fiscal.aplicar_irpf_default ? parseFloat(fiscal.irpf_default) : 0;
    if (fiscal.aplicar_irpf_default && (Number.isNaN(irpf) || irpf < 0 || irpf > 100)) {
      toast.error("El porcentaje de IRPF debe estar entre 0 y 100");
      return;
    }
    const cuotaRaw = fiscal.cuota_autonomo_mensual;
    const cuota = cuotaRaw === "" || cuotaRaw === null ? null : parseFloat(cuotaRaw);
    if (cuota !== null && (Number.isNaN(cuota) || cuota < 0)) {
      toast.error("La cuota de autónomo debe ser un número positivo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        fiscal_facturas: {
          ...profileFiscalFacturas,
          iva_default: Number(fiscal.iva_default),
          irpf_default: irpf,
          aplicar_irpf_default: fiscal.aplicar_irpf_default,
          regimen_iva: fiscal.regimen_iva,
        },
      };
      if (esFisica) {
        payload.fiscal_autonomo = {
          ...profileFiscalAutonomo,
          cuota_autonomo_mensual: cuota,
        };
      }
      await updateCompanyProfile(payload);
      invalidateCompanyProfile();
      setStep(3);
    } catch (e) {
      toast.error(e.message || "No se pudo guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-page">
      {/* Cabecera */}
      <div className="text-center mb-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">
          Bienvenido a LiquidArc
        </h1>
        <p className="text-slate-500 text-sm">
          Configura lo mínimo para poder emitir facturas. Solo te llevará un minuto.
        </p>
      </div>

      {/* Barra de progreso */}
      <div className="flex items-center justify-center gap-2 mb-8" data-testid="onboarding-progress">
        {STEPS.map((s, i) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  step >= s.num
                    ? "bg-slate-900 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {s.num}
              </div>
              <span
                className={`text-sm ${
                  step >= s.num ? "text-slate-900 font-medium" : "text-slate-400"
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-10 h-px ${step > s.num ? "bg-slate-900" : "bg-slate-200"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Paso 1: Tu empresa */}
      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="onboarding-step-1">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Tu empresa</h2>
          </div>

          <Field label="Tipo de persona" required>
            <Select
              value={identidad.tipo_persona}
              onValueChange={(v) => {
                setIdentidad((prev) => ({ ...prev, tipo_persona: v }));
                // El default de IRPF sigue al tipo de persona (A1): una sociedad
                // no aplica retención en sus facturas salvo casos concretos.
                setFiscal((prev) => ({
                  ...prev,
                  aplicar_irpf_default: v === "fisica",
                  irpf_default: v === "fisica" ? "7" : "0",
                }));
              }}
            >
              <SelectTrigger data-testid="onboarding-tipo-persona-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fisica">Persona física (autónomo)</SelectItem>
                <SelectItem value="juridica">Persona jurídica (sociedad)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label={esFisica ? "Nombre y apellidos" : "Razón social"} required>
            <Input
              value={identidad.name}
              onChange={(e) => setIdentidad((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={esFisica ? "Nombre y apellidos" : "Mi Empresa S.L."}
              data-testid="onboarding-name-input"
            />
          </Field>

          <Field label="NIF" required help="NIF/CIF con el que emites facturas.">
            <Input
              value={identidad.nif}
              onChange={(e) => setIdentidad((prev) => ({ ...prev, nif: e.target.value }))}
              placeholder="B12345678"
              data-testid="onboarding-nif-input"
            />
          </Field>

          <Field label="Dirección" required>
            <Input
              value={identidad.address}
              onChange={(e) => setIdentidad((prev) => ({ ...prev, address: e.target.value }))}
              placeholder="Calle, número, piso"
              data-testid="onboarding-address-input"
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Ciudad">
              <Input
                value={identidad.city}
                onChange={(e) => setIdentidad((prev) => ({ ...prev, city: e.target.value }))}
                placeholder="Madrid"
                data-testid="onboarding-city-input"
              />
            </Field>
            <Field label="Provincia">
              <Input
                value={identidad.provincia}
                onChange={(e) => setIdentidad((prev) => ({ ...prev, provincia: e.target.value }))}
                placeholder="Madrid"
                data-testid="onboarding-provincia-input"
              />
            </Field>
            <Field label="Código postal">
              <Input
                value={identidad.postal_code}
                onChange={(e) => setIdentidad((prev) => ({ ...prev, postal_code: e.target.value }))}
                placeholder="28001"
                data-testid="onboarding-cp-input"
              />
            </Field>
          </div>

          <Field label="Email" help="Aparecerá en tus facturas.">
            <Input
              type="email"
              value={identidad.email}
              onChange={(e) => setIdentidad((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="facturas@miempresa.com"
              data-testid="onboarding-email-input"
            />
          </Field>

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-slate-500"
              data-testid="onboarding-skip"
            >
              Completar más tarde
            </Button>
            <Button
              onClick={handleSaveStep1}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="onboarding-next"
            >
              {saving ? "Guardando..." : "Continuar"}
            </Button>
          </div>
        </div>
      )}

      {/* Paso 2: Tu fiscalidad */}
      {step === 2 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="onboarding-step-2">
          <div className="flex items-center gap-2 mb-2">
            <Percent className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Tu fiscalidad</h2>
          </div>

          <Field label="IVA por defecto" help="El tipo que se propondrá al crear cada factura. Podrás cambiarlo factura a factura.">
            <Select
              value={fiscal.iva_default}
              onValueChange={(v) => setFiscal((prev) => ({ ...prev, iva_default: v }))}
            >
              <SelectTrigger data-testid="onboarding-iva-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0 %</SelectItem>
                <SelectItem value="4">4 %</SelectItem>
                <SelectItem value="10">10 %</SelectItem>
                <SelectItem value="21">21 %</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Régimen de IVA" help="Exento (art. 20 LIVA): tus facturas se emitirán siempre sin IVA, por ejemplo formación o sanidad.">
            <Select
              value={fiscal.regimen_iva}
              onValueChange={(v) => setFiscal((prev) => ({ ...prev, regimen_iva: v }))}
            >
              <SelectTrigger data-testid="onboarding-regimen-iva-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="exento">Exento (art. 20)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-700">¿Aplicas retención IRPF?</p>
              <p className="text-xs text-slate-400">
                {esFisica
                  ? "Habitual en autónomos profesionales: se descuenta en cada factura emitida."
                  : "Las sociedades normalmente no aplican retención en sus facturas. Actívalo solo si tu actividad concreta lo requiere."}
              </p>
            </div>
            <Switch
              checked={fiscal.aplicar_irpf_default}
              onCheckedChange={(v) => setFiscal((prev) => ({ ...prev, aplicar_irpf_default: v }))}
              data-testid="onboarding-irpf-switch"
            />
          </div>

          {fiscal.aplicar_irpf_default && (
            <Field label="% de IRPF" help={esFisica ? "Lo habitual es 15 % (7 % los tres primeros años de actividad)." : "Indica el porcentaje que corresponde a tu caso concreto."}>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={fiscal.irpf_default}
                onChange={(e) => setFiscal((prev) => ({ ...prev, irpf_default: e.target.value }))}
                placeholder="15"
                data-testid="onboarding-irpf-input"
              />
            </Field>
          )}

          {esFisica && (
            <Field
              label="Cuota de autónomo (€/mes)"
              help="Opcional. Tu cuota real de la Seguridad Social; se usa en los cálculos de tesorería."
            >
              <Input
                type="number"
                min="0"
                step="0.01"
                value={fiscal.cuota_autonomo_mensual}
                onChange={(e) => setFiscal((prev) => ({ ...prev, cuota_autonomo_mensual: e.target.value }))}
                placeholder="Ej: 294"
                data-testid="onboarding-cuota-autonomo-input"
              />
            </Field>
          )}

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="text-slate-500"
                data-testid="onboarding-skip"
              >
                Completar más tarde
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                data-testid="onboarding-back"
              >
                Atrás
              </Button>
            </div>
            <Button
              onClick={handleSaveStep2}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="onboarding-next"
            >
              {saving ? "Guardando..." : "Continuar"}
            </Button>
          </div>
        </div>
      )}

      {/* Paso 3: Listo */}
      {step === 3 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6" data-testid="onboarding-step-3">
          <div className="text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" strokeWidth={1.5} />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Todo listo</h2>
            <p className="text-sm text-slate-500 mt-1">
              Ya puedes emitir facturas con estos datos:
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-1" data-testid="onboarding-summary">
            <p>
              <span className="text-slate-500">Emisor:</span>{" "}
              <span className="font-medium text-slate-900">{identidad.name}</span>{" "}
              <span className="text-slate-500">({identidad.nif})</span>
            </p>
            <p>
              <span className="text-slate-500">IVA por defecto:</span>{" "}
              <span className="font-medium text-slate-900">
                {fiscal.regimen_iva === "exento" ? "Exento (art. 20)" : `${fiscal.iva_default} %`}
              </span>
            </p>
            <p>
              <span className="text-slate-500">Retención IRPF:</span>{" "}
              <span className="font-medium text-slate-900">
                {fiscal.aplicar_irpf_default ? `${fiscal.irpf_default} %` : "No"}
              </span>
            </p>
          </div>

          <div className="grid gap-3">
            <button
              type="button"
              onClick={() => navigate("/banco/movimientos")}
              className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-3 text-left hover:bg-slate-50 transition-colors"
              data-testid="onboarding-link-banco"
            >
              <Landmark className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-900">Importa tus movimientos del banco</p>
                <p className="text-xs text-slate-500">La conciliación bancaria es la base de cobros y pagos.</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/facturas/emitidas")}
              className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-3 text-left hover:bg-slate-50 transition-colors"
              data-testid="onboarding-link-facturas"
            >
              <FileText className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-900">Crea tu primera factura</p>
                <p className="text-xs text-slate-500">Con tus datos de emisor y fiscalidad ya configurados.</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/ajustes")}
              className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-3 text-left hover:bg-slate-50 transition-colors"
              data-testid="onboarding-link-ajustes"
            >
              <SettingsIcon className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-900">Completa tus Ajustes</p>
                <p className="text-xs text-slate-500">Logo, series de facturación, IBAN y más detalles.</p>
              </div>
            </button>
          </div>

          <Button
            onClick={() => navigate("/dashboard")}
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
            data-testid="onboarding-finish"
          >
            Empezar a usar LiquidArc
          </Button>
        </div>
      )}
    </div>
  );
}
